import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import AuthLayout from '../../components/layout/AuthLayout';
import BaseDropdown from '../../components/form/BaseDropdown.jsx';
import BaseInput from '../../components/form/BaseInput.jsx';
import BaseButton from '../../components/form/BaseButton.jsx';
import BaseCard from '../../components/misc/BaseCard.jsx';
import {
    useAddOrganizationMutation,
    useEditOrganizationMutation,
    useGetOrganizationOwnershipTypesQuery,
    useGetOrganizationQuery,
    useLazyGetStockSymbolsQuery
} from '../../api/organizations';
import {
    PERMISSION_FILES,
    PERMISSION_OWNED_ORGANIZATIONS,
    PERMISSION_PARTNER_ORGANIZATIONS
} from '../../utils/constants.js';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import OrganizationDeleteConfirmation from '../../components/misc/organizations/OrganizationDeleteConfirmation.jsx';
import { Calendar } from 'primereact/calendar';
import BaseSwitch from '../../components/form/BaseSwitch.jsx';
import BaseRadio from '../../components/form/BaseRadio.jsx';
import IconClose from '../../components/images/IconClose.jsx';
import { useGetCountriesQuery, useGetEconomicRegionsQuery, useGetSkillsQuery } from '../../api/misc.js';
import { addTreeSelection, findItem, removeAttribute } from '../../utils/helper.js';
import IconAdd from '../../components/images/IconAdd.jsx';
import BaseOverlay from '../../components/layout/BaseOverlay.jsx';
import BaseTreeSelect from '../../components/form/BaseTreeSelect.jsx';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Badge } from 'primereact/badge';
import { MultiSelect } from 'primereact/multiselect';
import { useDebounce } from '../../hooks/useDebounce.js';
import { Checkbox } from 'primereact/checkbox';
import AssociateFiles from '../../components/misc/AssociateFiles.jsx';
import { transformFilesForRequest } from '../../utils/file.js';

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    ownership: Yup.string().required('Ownership is required'),
    domain: Yup.string()
        .nullable()
        .test('is-valid-domain-or-empty', 'Enter a valid domain name (e.g., kondiment.com, majoritas.com)', (value) => {
            if (!value) return true;
            const domainRegex = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
            return domainRegex.test(value);
        }),

    industry: Yup.string().nullable(),
    country_id: Yup.string().nullable(),
    stock_symbol_id: Yup.string().nullable(),
    founded: Yup.string().nullable(),
    employees: Yup.string()
        .nullable()
        .matches(/^\d*$/, 'Employees must be a number')
        .test('positive', 'Must be a positive number', (value) => !value || parseInt(value, 10) > 0),
    revenue: Yup.string()
        .nullable()
        .matches(/^\d*$/, 'Revenue must be a number')
        .test('positive', 'Must be a positive number', (value) => !value || parseInt(value, 10) > 0),
    profit: Yup.string()
        .nullable()
        .matches(/^\d*$/, 'Profit must be a number')
        .test('positive', 'Must be a positive number', (value) => !value || parseInt(value, 10) > 0),
    expenses: Yup.string()
        .nullable()
        .matches(/^\d*$/, 'Expenses must be a number')
        .test('positive', 'Must be a positive number', (value) => !value || parseInt(value, 10) > 0),
    assets: Yup.string().nullable(),
    liabilities: Yup.string().nullable(),
    news_enabled: Yup.boolean(),
    news_frequency: Yup.string(),
    news_keywords: Yup.array().of(Yup.string().trim().max(50, 'Keyword too long')).nullable()
});

const AddEditOrganization = ({ isEditMode = false }) => {
    let organization = null;
    const { id } = useParams();
    const navigate = useNavigate();
    const { permissions: userPermissions } = useCurrentUser();
    const [deleteVisible, setDeleteVisible] = useState(false);
    const [newsKeyword, setNewsKeyword] = useState('');

    const [selectedNeeds, setSelectedNeeds] = useState('');
    const [selectedCapabilities, setSelectedCapabilities] = useState('');
    const [showOverlay, setShowOverlay] = useState(false);
    const [showReachOverlay, setShowReachOverlay] = useState(false);
    const [needsOrCapabilities, setNeedsOrCapabilities] = useState('');
    const [reachOrPresence, setReachOrPresence] = useState('');
    const [initialReach, setInitialReach] = useState([]);
    const [selectedReachTree, setSelectedReachTree] = useState('');
    const [selectedPresenceTree, setSelectedPresenceTree] = useState('');

    const [initialStockSymbolRender, setInitialStockSymbolRender] = useState(true);
    const [stockSymbolList, setStockSymbolList] = useState([]);
    const [stockSymbolSearch, setStockSymbolSearch] = useState('');
    const debouncedStockSymbolSearch = useDebounce(stockSymbolSearch, 500);

    const [showFileSearch, setShowFileSearch] = useState(true);

    if (isEditMode && id) {
        const { data: organizationResponse } = useGetOrganizationQuery(id);

        organization = useMemo(() => {
            if (organizationResponse?.response?.organization) {
                return organizationResponse.response.organization;
            }

            return null;
        }, [organizationResponse]);
    }

    const canDelete = (() => {
        if (organization) {
            if (
                ['fully-owned', 'partially-owned'].includes(organization.ownership) &&
                userPermissions[PERMISSION_OWNED_ORGANIZATIONS]?.delete
            ) {
                return true;
            }
            if (
                organization.ownership === 'partner-company' &&
                userPermissions[PERMISSION_PARTNER_ORGANIZATIONS]?.delete
            ) {
                return true;
            }
        }

        return false;
    })();

    const { data: ownershipTypes, isLoading: ownershipTypesLoading } = useGetOrganizationOwnershipTypesQuery();

    const ownershipTypesList = Array.isArray(ownershipTypes?.response?.ownership_types)
        ? ownershipTypes.response.ownership_types.map((type) => ({
              label: type.label,
              value: type.value
          }))
        : [];

    const { data: countriesData } = useGetCountriesQuery({});
    const countries = useMemo(() => {
        if (countriesData?.response?.countries) {
            return countriesData.response.countries.map((country) => ({ label: country.name, value: country.id }));
        }

        return [];
    }, [countriesData]);

    const [getStockSymbols, { isLoading: isLoadingSymbols }] = useLazyGetStockSymbolsQuery();

    const [addOrganization, { isLoading: isAdding }] = useAddOrganizationMutation();
    const [editOrganization, { isLoading: isEditing }] = useEditOrganizationMutation();

    const {
        handleSubmit,
        setValue,
        control,
        watch,
        setError,
        clearErrors,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            name: '',
            ownership: '',
            domain: '',
            industry: '',
            country_id: '',
            founded: '',
            employees: '',
            revenue: '',
            profit: '',
            expenses: '',
            assets: '',
            liabilities: '',
            news_enabled: false,
            news_frequency: 'daily',
            news_keywords: [],
            needs: [],
            capabilities: [],
            files: [],
            reach: [],
            presence: [],
            international: false,
            stock_symbol_id: ''
        }
    });

    const newsEnabled = watch('news_enabled');
    const international = watch('international');
    const newsKeywords = watch('news_keywords') || [];

    const {
        fields: fieldsNeeds,
        append: appendNeed,
        remove: removeNeed
    } = useFieldArray({
        control,
        name: 'needs'
    });

    const {
        fields: fieldsCapabilities,
        append: appendCapability,
        remove: removeCapability
    } = useFieldArray({
        control,
        name: 'capabilities'
    });

    const {
        fields: fieldsFiles,
        append: appendFile,
        remove: removeFile
    } = useFieldArray({
        control,
        name: 'files'
    });

    const {
        fields: fieldsReach,
        append: appendReach,
        remove: removeReach
    } = useFieldArray({
        control,
        name: 'reach'
    });

    const {
        fields: fieldsPresence,
        append: appendPresence,
        remove: removePresence
    } = useFieldArray({
        control,
        name: 'presence'
    });

    const treeElement = useCallback((key, item) => {
        return {
            key,
            label: item.name,
            data: item.id,
            ...(item.sub_skills && item.sub_skills.length > 0
                ? {
                      children: item.sub_skills.map((sub, index) => treeElement(`${key}-${index}`, sub))
                  }
                : {})
        };
    }, []);

    const categoryMultiselect = (item) => {
        return {
            label: item.name,
            ...(item.countries && item.countries.length > 0
                ? {
                      items: item.countries.map((value) => {
                          return {
                              label: value.name,
                              value: value.id
                          };
                      })
                  }
                : {})
        };
    };

    const { data: skillsData } = useGetSkillsQuery({});
    const skills = useMemo(() => {
        const skillOptions = [];
        if (skillsData?.response?.skills) {
            skillsData.response.skills.forEach((item, index) => {
                skillOptions.push(treeElement(index, item));
            });
        }
        return skillOptions;
    }, [skillsData, treeElement]);

    const { data: economicRegionsData } = useGetEconomicRegionsQuery({});
    const economicRegions = useMemo(() => {
        const economicRegionsOptions = [];
        if (economicRegionsData?.response?.economic_regions) {
            economicRegionsData.response.economic_regions.map((region) => {
                economicRegionsOptions.push(categoryMultiselect(region));
            });
        }
        return economicRegionsOptions;
    }, [economicRegionsData]);

    const reachTreeElement = useCallback((key, region) => {
        return {
            key,
            label: region.label,
            data: null,
            noAppend: true,
            children: region.items.map((item, idx) => ({
                key: `${key}-${idx}`,
                label: item.label,
                data: item.value
            }))
        };
    }, []);

    const presenceTreeElement = useCallback((key, country) => {
        return {
            key,
            label: country.label,
            data: country.value
        };
    }, []);

    const reachOptions = useMemo(() => {
        const result = [];
        if (economicRegions?.length) {
            economicRegions.forEach((region, index) => {
                result.push(reachTreeElement(index, region));
            });
        }
        return result;
    }, [economicRegions, reachTreeElement]);

    const presenceOptions = useMemo(() => {
        const result = [];
        if (countries?.length) {
            countries.forEach((country, index) => {
                result.push(presenceTreeElement(index, country));
            });
        }
        return result;
    }, [countries, presenceTreeElement]);

    const childrenCount = useMemo(() => {
        if (!selectedReachTree || typeof selectedReachTree !== 'object') return 0;
        return Object.entries(selectedReachTree).reduce((count, [key, type]) => {
            if (!type.checked) return count;
            const parts = key.split('-');
            let node = reachOptions[parseInt(parts[0], 10)];
            for (let i = 1; i < parts.length; i++) {
                node = node.children[parseInt(parts[i], 10)];
            }
            if (!node.children || node.children.length === 0) {
                return count + 1;
            }
            return count;
        }, 0);
    }, [selectedReachTree, reachOptions]);

    const closeOverlay = (setOverlay, setMode) => {
        setOverlay(false);
        setMode('');
    };

    const addAttributes = (mode, setOverlay, setMode) => {
        setOverlay(true);
        setMode(mode);
    };

    const setAttribute = (treeArray, field, func, property) => {
        let arr = {};

        const processNode = (node, selectedIds) => {
            let key = findItem(node, node.data);
            if (key === undefined || key === null) return { checked: false, partialChecked: false };

            if (node.children && node.children.length > 0) {
                let childrenStates = node.children.map((child) => processNode(child, selectedIds));
                let allChildrenChecked = childrenStates.every((child) => child.checked);
                let someChildrenChecked = childrenStates.some((child) => child.checked || child.partialChecked);
                arr[key] = {
                    checked: allChildrenChecked,
                    partialChecked: someChildrenChecked && !allChildrenChecked
                };
                return arr[key];
            } else {
                let isSelected = selectedIds.includes(node.data);
                arr[key] = { checked: isSelected, partialChecked: false };
                return arr[key];
            }
        };

        let selectedIds = field.map((item) => item[property]);
        treeArray.forEach((node) => processNode(node, selectedIds));
        func(arr);
    };

    useEffect(() => {
        setAttribute(skills, fieldsCapabilities, setSelectedCapabilities, 'capability_id');
        setAttribute(skills, fieldsNeeds, setSelectedNeeds, 'need_id');
        setAttribute(reachOptions, fieldsReach, setSelectedReachTree, 'economic_region_id');
        setAttribute(presenceOptions, fieldsPresence, setSelectedPresenceTree, 'office_id');
    }, [skills, fieldsCapabilities, fieldsNeeds, reachOptions, fieldsReach, presenceOptions, fieldsPresence]);

    const handleAddNewsKeyword = () => {
        const trimmed = newsKeyword.trim();
        if (trimmed !== '') {
            setValue('news_keywords', [...newsKeywords, trimmed]);
            setNewsKeyword('');
        }
    };

    const selectReach = useCallback(
        (value) => {
            if (
                economicRegions &&
                economicRegions.every((region) => region.items?.every((item) => value.includes(item.value)))
            ) {
                setValue('international', true);
            } else {
                value.forEach((item) => {
                    if (!fieldsReach.some((field) => field.economic_region_id === item)) {
                        const foundItem = economicRegions
                            .flatMap((region) => region.items)
                            .find((element) => element.value === item);

                        const itemLabel = foundItem ? foundItem.label : null;
                        appendReach({
                            name: itemLabel,
                            economic_region_id: item
                        });
                    }
                });

                const indicesToRemove = [];
                fieldsReach.forEach((item, index) => {
                    if (!value.includes(item.economic_region_id)) {
                        indicesToRemove.push(index);
                    }
                });

                indicesToRemove.reverse().forEach((index) => removeReach(index));
            }
        },
        [appendReach, economicRegions, fieldsReach, removeReach, setValue]
    );

    const onSubmit = async (data) => {
        if ((data.ownership === 'fully-owned' || data.ownership === 'partially-owned') && !data.domain) {
            setError('domain', {
                type: 'manual',
                message: 'Domain is required for selected ownership'
            });
            return;
        }

        const finalFiles = transformFilesForRequest(data.files);

        const payload = {
            name: data.name,
            ownership: data.ownership,
            domain: data.domain,
            industry: data.industry,
            country_id: data.country_id,
            founded: data.founded,
            employees: data.employees,
            revenue: data.revenue,
            profit: data.profit,
            expenses: data.expenses,
            assets: data.assets,
            liabilities: data.liabilities,
            news_enabled: data.news_enabled,
            news_frequency: data.news_frequency,
            news_keywords: data.news_keywords,
            needs: data.needs,
            capabilities: data.capabilities,
            files: finalFiles,
            international: data.international,
            presence: data.presence,
            ...(!data.international ? { reach: data.reach } : {}),
            ...(data.stock_symbol_id ? { stock_symbol_id: data.stock_symbol_id } : {})
        };

        try {
            if (isEditMode) {
                await editOrganization({ id, data: payload }).unwrap();
                navigate(`/organizations/${id}`);
            } else {
                await addOrganization(payload).then((apiResponse) => {
                    if (apiResponse.data?.success) {
                        navigate(`/organizations/${apiResponse.data?.response.id}`);
                    }
                });
            }
        } catch (error) {
            console.error('Submission failed', error);
            if (error?.data?.errors) {
                Object.entries(error.data.errors).forEach(([field, messages]) => {
                    setError(field, { type: 'server', message: messages[0] });
                });
            }
        }
    };

    const handleDeleteConfirmation = () => {
        setDeleteVisible(true);
    };

    useEffect(() => {
        const numericFields = ['employees', 'revenue', 'profit', 'expenses'];
        if (isEditMode && organization) {
            Object.entries(organization).forEach(([key, value]) => {
                if (numericFields.includes(key)) {
                    setValue(key, value > 0 ? String(value) : '');
                } else if (typeof value === 'boolean' || Array.isArray(value)) {
                    if (key === 'files') {
                        setValue(
                            key,
                            organization.files?.map((file) => ({
                                file_id: file.file_id,
                                name: file.title,
                                path: file.url,
                                extension: file.extension,
                                type: file.category
                            })) || []
                        );
                    } else {
                        setValue(key, value);
                    }
                } else {
                    setValue(key, value !== null && value !== undefined ? String(value) : '');
                }
                setInitialReach(organization.reach.map((item) => item.economic_region_id));
            });
        }
    }, [isEditMode, organization, setValue]);

    useEffect(() => {
        if (international) {
            selectReach(initialReach);
            if (reachOrPresence === 'reach' && showReachOverlay) {
                setShowReachOverlay(false);
            }
        }
    }, [international, initialReach, reachOrPresence, selectReach, showReachOverlay]);

    useEffect(() => {
        if (debouncedStockSymbolSearch || initialStockSymbolRender) {
            (async () => {
                const result = await getStockSymbols({
                    search: debouncedStockSymbolSearch,
                    ...(isEditMode && organization && organization.stock_symbol_id
                        ? { stock_symbol_id: organization.stock_symbol_id }
                        : {})
                });
                setStockSymbolList(
                    result?.data?.response?.symbols.map((symbol) => ({
                        label: `${symbol.symbol} - ${
                            symbol.name.length > 25 ? symbol.name.slice(0, 25) + '...' : symbol.name
                        }`,
                        value: symbol.id
                    })) || []
                );
                setInitialStockSymbolRender(false);
            })();
        }
    }, [debouncedStockSymbolSearch, getStockSymbols, initialStockSymbolRender, isEditMode, organization]);

    const renderNeedsAndCapabilities = () => (
        <BaseCard className='border-0 bg-theme1-tan p-0 pb-6'>
            <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                <div className='text-2xl font-bold text-theme1-title2'>Needs &amp; Capabilities</div>
            </div>
            <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                <div className='max-w-3xl w-full mx-auto px-4'>
                    <Accordion className='custom-accordion' activeIndex={isEditMode ? null : 0}>
                        <AccordionTab
                            key='needs-tab'
                            className='custom-accordion-tab-no-padding'
                            header={
                                <>
                                    <span className='text-lg font-bold text-theme1-primary'>Needs</span>
                                    <Badge value={fieldsNeeds?.length} className='ns-badge'></Badge>
                                </>
                            }
                        >
                            <div className='flex items-center flex-wrap'>
                                {fieldsNeeds.length > 0 &&
                                    fieldsNeeds.map((need, index) => (
                                        <span
                                            key={need.id}
                                            className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center w-fit'
                                        >
                                            <span>{need.name}</span>
                                            <span
                                                onClick={() =>
                                                    removeAttribute(
                                                        skills,
                                                        index,
                                                        need.need_id,
                                                        setSelectedNeeds,
                                                        'need_id',
                                                        fieldsNeeds,
                                                        removeNeed
                                                    )
                                                }
                                            >
                                                <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                            </span>
                                        </span>
                                    ))}
                                <span
                                    onClick={() => addAttributes('needs', setShowOverlay, setNeedsOrCapabilities)}
                                    className='mr-4 mb-4 px-4 py-2 border-2 border-dashed border-theme1-gold1 rounded-4xl text-theme1-gold1 font-bold flex items-center w-fit'
                                >
                                    <span>Add Need</span>
                                    <span>
                                        <IconAdd className='w-6 h-6 fill-theme1-gold1' />
                                    </span>
                                </span>
                            </div>
                        </AccordionTab>
                    </Accordion>
                </div>
            </BaseCard>
            <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                <div className='max-w-3xl w-full mx-auto px-4'>
                    <Accordion className='custom-accordion' activeIndex={isEditMode ? null : 0}>
                        <AccordionTab
                            key='capabilities-tab'
                            className='custom-accordion-tab-no-padding'
                            header={
                                <>
                                    <span className='text-lg font-bold text-theme1-primary'>Capabilities</span>
                                    <Badge value={fieldsCapabilities?.length} className='ns-badge'></Badge>
                                </>
                            }
                        >
                            <div className='flex items-center flex-wrap'>
                                {fieldsCapabilities.length > 0 &&
                                    fieldsCapabilities.map((capability, index) => (
                                        <span
                                            key={capability.id}
                                            className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center w-fit'
                                        >
                                            <span>{capability.name}</span>
                                            <span
                                                onClick={() =>
                                                    removeAttribute(
                                                        skills,
                                                        index,
                                                        capability.capability_id,
                                                        setSelectedCapabilities,
                                                        'capability_id',
                                                        fieldsCapabilities,
                                                        removeCapability
                                                    )
                                                }
                                            >
                                                <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                            </span>
                                        </span>
                                    ))}
                                <span
                                    onClick={() =>
                                        addAttributes('capabilities', setShowOverlay, setNeedsOrCapabilities)
                                    }
                                    className='mr-4 mb-4 px-4 py-2 border-2 border-dashed border-theme1-gold1 rounded-4xl text-theme1-gold1 font-bold flex items-center w-fit'
                                >
                                    <span>Add Capability</span>
                                    <span>
                                        <IconAdd className='w-6 h-6 fill-theme1-gold1' />
                                    </span>
                                </span>
                            </div>
                        </AccordionTab>
                    </Accordion>
                </div>
            </BaseCard>
        </BaseCard>
    );

    const renderReachAndPresence = () => (
        <BaseCard className='border-0 bg-theme1-tan p-0 pb-6'>
            <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                <div className='text-2xl font-bold text-theme1-title2'>Reach &amp; Presence</div>
            </div>
            <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                <div className='max-w-3xl w-full mx-auto px-4'>
                    <Accordion className='custom-accordion' activeIndex={isEditMode ? null : 0}>
                        <AccordionTab
                            key='reach-tab'
                            className={`custom-accordion-tab-no-padding`}
                            header={
                                <>
                                    <span className='text-lg font-bold text-theme1-primary'>Reach</span>
                                    <Badge
                                        value={international ? 'International' : childrenCount}
                                        className='ns-badge'
                                    ></Badge>
                                </>
                            }
                        >
                            <div className={'mb-4'}>
                                <BaseSwitch
                                    control={control}
                                    name='international'
                                    label={'International'}
                                    clearErrors={clearErrors}
                                    errors={[]}
                                />
                            </div>
                            <div className='flex items-center flex-wrap'>
                                {!international &&
                                    fieldsReach.length > 0 &&
                                    fieldsReach.map((region, index) => (
                                        <span
                                            key={region.id}
                                            className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center w-fit'
                                        >
                                            <span>{region.name}</span>
                                            <span
                                                onClick={() => {
                                                    removeAttribute(
                                                        reachOptions,
                                                        index,
                                                        region.economic_region_id,
                                                        setSelectedReachTree,
                                                        'economic_region_id',
                                                        fieldsReach,
                                                        removeReach
                                                    );
                                                }}
                                            >
                                                <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                            </span>
                                        </span>
                                    ))}
                                {!international && (
                                    <span
                                        onClick={() => addAttributes('reach', setShowReachOverlay, setReachOrPresence)}
                                        className='mr-4 mb-4 px-4 py-2 border-2 border-dashed border-theme1-gold1 rounded-4xl text-theme1-gold1 font-bold flex items-center w-fit'
                                    >
                                        <span>Add Reach</span>
                                        <span>
                                            <IconAdd className='w-6 h-6 fill-theme1-gold1' />
                                        </span>
                                    </span>
                                )}
                            </div>
                        </AccordionTab>
                    </Accordion>
                </div>
            </BaseCard>
            <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                <div className='max-w-3xl w-full mx-auto px-4'>
                    <Accordion className='custom-accordion' activeIndex={isEditMode ? null : 0}>
                        <AccordionTab
                            key='presence-tab'
                            className='custom-accordion-tab-no-padding'
                            header={
                                <>
                                    <span className='text-lg font-bold text-theme1-primary'>Offices</span>
                                    <Badge value={fieldsPresence?.length} className='ns-badge'></Badge>
                                </>
                            }
                        >
                            <div className='flex items-center flex-wrap'>
                                {fieldsPresence.length > 0 &&
                                    fieldsPresence.map((office, index) => (
                                        <span
                                            key={office.id}
                                            className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center w-fit'
                                        >
                                            <span>{office.name}</span>
                                            <span
                                                onClick={() => {
                                                    removeAttribute(
                                                        presenceOptions,
                                                        index,
                                                        office.office_id,
                                                        setSelectedPresenceTree,
                                                        'office_id',
                                                        fieldsPresence,
                                                        removePresence
                                                    );
                                                }}
                                            >
                                                <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                            </span>
                                        </span>
                                    ))}
                                <span
                                    onClick={() => addAttributes('presence', setShowReachOverlay, setReachOrPresence)}
                                    className='mr-4 mb-4 px-4 py-2 border-2 border-dashed border-theme1-gold1 rounded-4xl text-theme1-gold1 font-bold flex items-center w-fit'
                                >
                                    <span>Add Office</span>
                                    <span>
                                        <IconAdd className='w-6 h-6 fill-theme1-gold1' />
                                    </span>
                                </span>
                            </div>
                        </AccordionTab>
                    </Accordion>
                </div>
            </BaseCard>
        </BaseCard>
    );

    return (
        <AuthLayout
            title={isEditMode ? 'Edit Organization' : 'Add Organization'}
            backRoute={isEditMode ? `/organizations/${id}` : '/organizations/list'}
        >
            <div className='max-w-3xl w-full mx-auto px-8 mt-6 mb-10'>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className='mb-4'>
                        <BaseInput
                            id='name'
                            name='name'
                            label='Name'
                            type='text'
                            errors={errors.name ? [errors.name.message] : []}
                            control={control}
                        />
                    </div>

                    <div className='mb-4'>
                        <BaseDropdown
                            id='ownership'
                            name='ownership'
                            label='Ownership'
                            placeholder='Select Ownership'
                            errors={errors.ownership ? [errors.ownership.message] : []}
                            control={control}
                            options={ownershipTypesList}
                            disabled={ownershipTypesLoading}
                            filter={false}
                        />
                    </div>

                    <div className='mb-4'>
                        <BaseDropdown
                            id='stock_symbol_id'
                            name='stock_symbol_id'
                            label='Stock Symbol'
                            placeholder='Select a Stock symbol'
                            inputClassName={'text-xs'}
                            errors={errors.stock_symbol_id ? [errors.stock_symbol_id.message] : []}
                            control={control}
                            options={stockSymbolList}
                            onFilter={(e) => {
                                setStockSymbolSearch(e.filter);
                            }}
                        />
                    </div>

                    <div className='mb-4'>
                        <BaseInput
                            id='domain'
                            name='domain'
                            label='Domain'
                            type='text'
                            errors={errors.domain ? [errors.domain.message] : []}
                            control={control}
                        />
                    </div>

                    <div className='mb-4'>
                        <label className='text-theme1-primary2 font-bold text-base mb-1'>Founded</label>
                        <Controller
                            name='founded'
                            control={control}
                            render={({ field }) => (
                                <Calendar
                                    id={'founded'}
                                    placeholder={'Founded'}
                                    value={field.value ? new Date(Number(field.value), 0) : null}
                                    onChange={(e) => {
                                        const year = e.value?.getFullYear().toString() || '';
                                        field.onChange(year);
                                    }}
                                    view='year'
                                    dateFormat='yy'
                                    className='w-full bg-white border-theme1-text3 border rounded-2xl px-4 py-2'
                                />
                            )}
                        />
                        {errors.founded && <div className='mt-2.5 text-theme1-error'>{errors.founded.message}</div>}
                    </div>
                    <div className='mb-4'>
                        <BaseDropdown
                            className='mt-4'
                            control={control}
                            label='Headquarters'
                            name='country_id'
                            clearErrors={clearErrors}
                            errors={[]}
                            options={countries}
                        />
                    </div>

                    {['industry', 'assets', 'liabilities', 'employees', 'revenue', 'profit', 'expenses'].map(
                        (field) => (
                            <div className='mb-4' key={field}>
                                <BaseInput
                                    id={field}
                                    name={field}
                                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                                    type='text'
                                    control={control}
                                    errors={errors[field] ? [errors[field].message] : []}
                                />
                            </div>
                        )
                    )}

                    <div className='mb-4 border p-4 rounded-2xl'>
                        <div className='text-theme1-primary font-bold text-xl mb-4'>News Settings</div>
                        <BaseSwitch
                            control={control}
                            name='news_enabled'
                            label={newsEnabled ? 'Disable news' : 'Enable news'}
                            clearErrors={clearErrors}
                            errors={[]}
                        />
                        {newsEnabled && (
                            <>
                                <div className='mt-4'>
                                    <div className='text-lg font-bold text-theme1-primary mb-1'>Frequency</div>
                                    <div className='flex items-center space-x-4'>
                                        <BaseRadio
                                            control={control}
                                            name='news_frequency'
                                            value='daily'
                                            textValue='Daily'
                                            clearErrors={clearErrors}
                                            errors={[]}
                                        />
                                        <BaseRadio
                                            control={control}
                                            name='news_frequency'
                                            value='weekly'
                                            textValue='Weekly'
                                            clearErrors={clearErrors}
                                            errors={[]}
                                        />
                                    </div>
                                </div>
                                <div className='mt-4'>
                                    <div className='text-lg font-bold text-theme1-primary mb-1'>News Keywords</div>
                                    <div className='flex items-center space-x-2'>
                                        <BaseInput
                                            type='text'
                                            placeholder='Enter keyword'
                                            value={newsKeyword}
                                            setValue={setNewsKeyword}
                                            errors={[]}
                                            className='w-3/4'
                                        />
                                        <BaseButton
                                            onClick={handleAddNewsKeyword}
                                            textValue='Add'
                                            className='btn-primary w-1/4 px-2 py-3'
                                        />
                                    </div>
                                    <div className='flex items-center flex-wrap mt-4'>
                                        {newsKeywords.length > 0 &&
                                            newsKeywords.map((keyword, index) => (
                                                <span
                                                    key={index}
                                                    className='mr-4 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                                >
                                                    <span>{keyword}</span>
                                                    <span
                                                        onClick={() =>
                                                            setValue(
                                                                'news_keywords',
                                                                newsKeywords.filter((_, i) => i !== index)
                                                            )
                                                        }
                                                        className='ml-2 cursor-pointer'
                                                    >
                                                        <IconClose className='w-5 h-5 fill-theme1-white' />
                                                    </span>
                                                </span>
                                            ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {renderReachAndPresence()}
                    {renderNeedsAndCapabilities()}

                    {!!userPermissions[PERMISSION_FILES]?.read && (
                        <AssociateFiles
                            fields={fieldsFiles}
                            append={appendFile}
                            remove={removeFile}
                            showSearch={showFileSearch}
                            setShowSearch={setShowFileSearch}
                        />
                    )}

                    <button
                        type='submit'
                        className='p-4 rounded-4xl text-lg font-bold leading-4.5 w-full btn-primary mt-8'
                        disabled={isAdding || isEditing}
                    >
                        {isEditMode ? 'Update Organization' : 'Add Organization'}
                    </button>
                </form>

                {canDelete && !organization?.has_users && isEditMode && (
                    <BaseButton
                        className='btn-primary-outline mt-4'
                        textValue='Delete Organization'
                        onClick={handleDeleteConfirmation}
                    />
                )}
            </div>

            <BaseOverlay show={showOverlay} className='overflow-auto h-[100dvh] rounded-none'>
                <div className='min-h-96 max-w-3xl w-full mx-auto px-4'>
                    <div className='flex justify-end py-4'>
                        <div
                            className='text-theme1-primary font-bold'
                            onClick={() => closeOverlay(setShowOverlay, setNeedsOrCapabilities)}
                        >
                            Close
                        </div>
                    </div>
                    <div className='text-center text-theme1-title2 font-bold text-[22px] mb-3'>
                        {needsOrCapabilities === 'needs' ? 'Needs' : 'Capabilities'}
                    </div>
                    {needsOrCapabilities === 'needs' && (
                        <div>
                            <div className='mb-4'>
                                <div className='card flex justify-content-center'>
                                    <BaseTreeSelect
                                        name='needs'
                                        label='Select Needs'
                                        inputClassName='md:w-20rem w-full'
                                        value={selectedNeeds}
                                        setValue={setSelectedNeeds}
                                        options={skills}
                                        selectionMode='checkbox'
                                        display='chip'
                                        placeholder='Choose needs'
                                        showClear={false}
                                        clearErrors={clearErrors}
                                        filter={true}
                                        filterMode={'strict'}
                                        filterDelay={10}
                                        onSelect={(value) =>
                                            addTreeSelection(
                                                skills,
                                                'needs',
                                                value,
                                                'need_id',
                                                appendNeed,
                                                removeNeed,
                                                fieldsNeeds,
                                                setSelectedNeeds
                                            )
                                        }
                                    />
                                </div>
                            </div>
                            <div className='flex items-center flex-wrap overflow-y-scroll'>
                                {fieldsNeeds.map((need, index) => (
                                    <span
                                        key={need.id}
                                        className='mr-4 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                    >
                                        <span>{need.name}</span>
                                        <span
                                            onClick={() =>
                                                removeAttribute(
                                                    skills,
                                                    index,
                                                    need.need_id,
                                                    setSelectedNeeds,
                                                    'need_id',
                                                    fieldsNeeds,
                                                    removeNeed
                                                )
                                            }
                                        >
                                            <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                        </span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {needsOrCapabilities === 'capabilities' && (
                        <div>
                            <div className='mb-4'>
                                <div className='card flex justify-content-center'>
                                    <BaseTreeSelect
                                        name='capabilities'
                                        label='Select Capabilities'
                                        inputClassName='md:w-20rem w-full'
                                        value={selectedCapabilities}
                                        setValue={setSelectedCapabilities}
                                        options={skills}
                                        selectionMode='checkbox'
                                        display='chip'
                                        placeholder='Choose capabilities'
                                        showClear={false}
                                        clearErrors={clearErrors}
                                        filter={true}
                                        filterMode={'strict'}
                                        filterDelay={10}
                                        onSelect={(value) =>
                                            addTreeSelection(
                                                skills,
                                                'capabilities',
                                                value,
                                                'capability_id',
                                                appendCapability,
                                                removeCapability,
                                                fieldsCapabilities,
                                                setSelectedCapabilities
                                            )
                                        }
                                    />
                                </div>
                            </div>
                            <div className='flex items-center flex-wrap overflow-y-scroll'>
                                {fieldsCapabilities.map((capability, index) => (
                                    <span
                                        key={capability.id}
                                        className='mr-4 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                    >
                                        <span>{capability.name}</span>
                                        <span
                                            onClick={() =>
                                                removeAttribute(
                                                    skills,
                                                    index,
                                                    capability.capability_id,
                                                    setSelectedCapabilities,
                                                    'capability_id',
                                                    fieldsCapabilities,
                                                    removeCapability
                                                )
                                            }
                                        >
                                            <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                        </span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </BaseOverlay>

            <BaseOverlay show={showReachOverlay} className='overflow-auto h-[100dvh] rounded-none'>
                <div className='min-h-96 max-w-3xl w-full mx-auto px-4'>
                    <div className='flex justify-end py-4'>
                        <div
                            className='text-theme1-primary font-bold'
                            onClick={() => closeOverlay(setShowReachOverlay, setReachOrPresence)}
                        >
                            Close
                        </div>
                    </div>
                    <div className='text-center text-theme1-title2 font-bold text-[22px] mb-3'>
                        {reachOrPresence === 'reach' ? 'Reach' : 'Presence'}
                    </div>
                    {reachOrPresence === 'reach' && (
                        <div>
                            <div className='mb-4'>
                                <div className='card flex justify-content-center'>
                                    <BaseTreeSelect
                                        name='reach'
                                        label='Select Reach'
                                        inputClassName='md:w-20rem w-full'
                                        value={selectedReachTree}
                                        setValue={setSelectedReachTree}
                                        options={reachOptions}
                                        selectionMode='checkbox'
                                        display='chip'
                                        placeholder='Choose regions'
                                        showClear={false}
                                        clearErrors={clearErrors}
                                        filter={true}
                                        filterMode={'strict'}
                                        filterDelay={10}
                                        template={() => (childrenCount > 0 ? `${childrenCount} selected` : 'Select')}
                                        onSelect={(value) =>
                                            addTreeSelection(
                                                reachOptions,
                                                'reach',
                                                value,
                                                'economic_region_id',
                                                appendReach,
                                                removeReach,
                                                fieldsReach,
                                                setSelectedReachTree
                                            )
                                        }
                                    />
                                </div>
                            </div>
                            <div className='flex items-center flex-wrap overflow-y-scroll'>
                                {fieldsReach.map((region, index) => (
                                    <span
                                        key={region.id}
                                        className='mr-4 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                    >
                                        <span>{region.name}</span>
                                        <span
                                            onClick={() =>
                                                removeAttribute(
                                                    reachOptions,
                                                    index,
                                                    region.economic_region_id,
                                                    setSelectedReachTree,
                                                    'economic_region_id',
                                                    fieldsReach,
                                                    removeReach
                                                )
                                            }
                                        >
                                            <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                        </span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {reachOrPresence === 'presence' && (
                        <div>
                            <div className='mb-4'>
                                <div className='card flex justify-content-center'>
                                    <BaseTreeSelect
                                        name='presence'
                                        label='Select Presence'
                                        inputClassName='md:w-20rem w-full'
                                        value={selectedPresenceTree}
                                        setValue={setSelectedPresenceTree}
                                        options={presenceOptions}
                                        selectionMode='checkbox'
                                        display='chip'
                                        placeholder='Choose offices'
                                        showClear={false}
                                        clearErrors={clearErrors}
                                        filter={true}
                                        filterMode={'strict'}
                                        filterDelay={10}
                                        onSelect={(value) =>
                                            addTreeSelection(
                                                presenceOptions,
                                                'presence',
                                                value,
                                                'office_id',
                                                appendPresence,
                                                removePresence,
                                                fieldsPresence,
                                                setSelectedPresenceTree
                                            )
                                        }
                                    />
                                </div>
                            </div>
                            <div className='flex items-center flex-wrap overflow-y-scroll'>
                                {fieldsPresence.map((office, index) => (
                                    <span
                                        key={office.id}
                                        className='mr-4 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                    >
                                        <span>{office.name}</span>
                                        <span
                                            onClick={() =>
                                                removeAttribute(
                                                    presenceOptions,
                                                    index,
                                                    office.office_id,
                                                    setSelectedPresenceTree,
                                                    'office_id',
                                                    fieldsPresence,
                                                    removePresence
                                                )
                                            }
                                        >
                                            <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                        </span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </BaseOverlay>

            {deleteVisible && (
                <OrganizationDeleteConfirmation
                    deleteVisible={deleteVisible}
                    setDeleteVisible={setDeleteVisible}
                    organization={organization}
                />
            )}
        </AuthLayout>
    );
};

export default AddEditOrganization;
